import { withMonthlyOverrides, type Move, type NodeBalance, type VulpariNode } from "@vulpari/core";
import {
  Background,
  Controls,
  ReactFlow,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type Connection,
  type OnSelectionChangeParams,
} from "@xyflow/react";
import { useCallback, useMemo, useRef, useState, type MouseEvent } from "react";

import { incomingInPrimary } from "../lib/groupTotal.js";
import { useStore, type GroupPrompt } from "../store.js";
import { GroupNode } from "./GroupNode.js";
import { MoneyNode, type MoneyNodeData } from "./MoneyNode.js";

// Uses React Flow's built-in "group" node type (so it sizes and stacks
// correctly), overriding the default square styling in CSS; our GroupNode
// supplies the rounded, tinted backdrop.
const nodeTypes = { money: MoneyNode, group: GroupNode };

const GROUP_PREFIX = "group:";

// Node footprint. Nodes are given a fixed width per type (below) so the group
// backdrop can hug them exactly; the height is estimated from the lines each
// type renders. These are estimates, never a live measurement, since measuring
// previously blanked the backdrop.
const NODE_W = 200;
// Cards (and non-monthly bills) carry a longer line, so they are a touch wider
// to keep it on one line rather than wrapping.
const NODE_W_WIDE = 270;
const NODE_H = 96;
// A collapsed group's summary card is a little taller for its extra line.
const COLLAPSED_H = 124;

// Even padding around a group's members, with extra room at the top for the
// heading.
const GROUP_PAD = 18;
const GROUP_TOP = 44;

/** A node's fixed render width, by type. */
function estimateNodeWidth(node: VulpariNode): number {
  if (node.type === "card") {
    return NODE_W_WIDE;
  }
  if (node.type === "bill" && node.meta.frequency !== "monthly") {
    return NODE_W_WIDE;
  }
  return NODE_W;
}

// Estimated height of a node, by the extra lines its type renders below the
// header and headline figure.
const LINE = 18;
function estimateNodeHeight(node: VulpariNode, computedBalance: number | undefined): number {
  let height = NODE_H;
  switch (node.type) {
    case "card": {
      // A progress bar or, with no reference amount, the hint line (which fits
      // on one line at the wider card width).
      height += node.meta.creditLimit <= 0 ? LINE : LINE + 8;
      // The monthly-interest line, when a balance is carried on a card with APR.
      const balance = computedBalance ?? node.meta.balance;
      if (node.meta.aprBasisPoints > 0 && balance > 0) {
        height += LINE;
      }
      break;
    }
    case "bill":
      if (node.meta.frequency !== "monthly") {
        height += LINE; // the monthly-equivalent line
      }
      break;
    case "oneoff":
      height += LINE; // the "Due ..." line
      break;
    case "account":
      if (node.meta.role === "bills") {
        height += LINE; // the "Total of bills covered" line
      }
      break;
  }
  return height;
}

/** What a node would group with if dropped where it is now: the element to
 * highlight, and the action to propose on drop. */
interface GroupingTarget {
  highlightId: string;
  prompt: NonNullable<GroupPrompt>;
}

function findGroupingTarget(
  nodes: VulpariNode[],
  groups: { id: string; name: string; collapsed: boolean; nodeIds: string[] }[],
  dragged: VulpariNode,
  x: number,
  y: number,
): GroupingTarget | null {
  const cx = x + NODE_W / 2;
  const cy = y + NODE_H / 2;
  const posOf = (id: string) => nodes.find((n) => n.id === id);

  // Inside an expanded group it is not already part of.
  for (const group of groups) {
    if (group.collapsed || group.nodeIds.includes(dragged.id)) {
      continue;
    }
    const members = group.nodeIds.map(posOf).filter((n): n is VulpariNode => n !== undefined);
    if (members.length === 0) {
      continue;
    }
    const minX = Math.min(...members.map((m) => m.positionX)) - GROUP_PAD;
    const minY = Math.min(...members.map((m) => m.positionY)) - GROUP_TOP;
    const maxX = Math.max(...members.map((m) => m.positionX + NODE_W)) + GROUP_PAD;
    const maxY = Math.max(...members.map((m) => m.positionY + NODE_H)) + GROUP_PAD;
    if (cx >= minX && cx <= maxX && cy >= minY && cy <= maxY) {
      return {
        highlightId: `${GROUP_PREFIX}${group.id}`,
        prompt: {
          kind: "add",
          nodeId: dragged.id,
          nodeName: dragged.name,
          groupId: group.id,
          groupName: group.name,
        },
      };
    }
  }

  // Over another node.
  for (const other of nodes) {
    if (other.id === dragged.id) {
      continue;
    }
    const within =
      cx >= other.positionX &&
      cx <= other.positionX + NODE_W &&
      cy >= other.positionY &&
      cy <= other.positionY + NODE_H;
    if (!within) {
      continue;
    }
    const otherGroup = groups.find((g) => g.nodeIds.includes(other.id));
    if (otherGroup) {
      if (otherGroup.nodeIds.includes(dragged.id)) {
        return null;
      }
      return {
        highlightId: `${GROUP_PREFIX}${otherGroup.id}`,
        prompt: {
          kind: "add",
          nodeId: dragged.id,
          nodeName: dragged.name,
          groupId: otherGroup.id,
          groupName: otherGroup.name,
        },
      };
    }
    return {
      highlightId: other.id,
      prompt: {
        kind: "create",
        nodeIds: [dragged.id, other.id],
        nodeNames: [dragged.name, other.name],
      },
    };
  }

  return null;
}

/** Calm, semantic colour for a wire, by the kinds of node it connects. */
function edgeColour(source: VulpariNode | undefined, target: VulpariNode | undefined): string {
  if (source?.type === "income") {
    return "var(--colour-positive)"; // money arriving
  }
  if (target?.type === "pot") {
    return "var(--colour-positive)"; // saving
  }
  if (target?.type === "card") {
    return "var(--colour-accent)"; // paying down a card
  }
  if (target?.type === "bill" || target?.type === "oneoff" || target?.type === "external") {
    return "var(--colour-accent)"; // money leaving for an obligation
  }
  return "var(--colour-neutral)"; // ordinary transfer between accounts
}

interface Props {
  /** Nodes to display, with balances carried forward to the viewed month. Their
   * ids and positions match the store, so editing and dragging still work. */
  nodes: VulpariNode[];
  balances: NodeBalance[];
  moves: Move[];
}

export function Canvas({ nodes: inputNodes, balances, moves }: Props) {
  const storeFlows = useStore((state) => state.flows);
  const groups = useStore((state) => state.groups);
  const moveNode = useStore((state) => state.moveNode);
  const connect = useStore((state) => state.connect);
  const removeFlow = useStore((state) => state.removeFlow);
  const deleteNodes = useStore((state) => state.deleteNodes);
  const setSelection = useStore((state) => state.setSelection);
  const selectedNodeIds = useStore((state) => state.selectedNodeIds);
  const selectedEdgeIds = useStore((state) => state.selectedEdgeIds);
  const month = useStore((state) => state.context.month);
  const primaryCurrency = useStore((state) => state.context.primaryCurrency) ?? "GBP";
  const rates = useStore((state) => state.context.rates);

  // Display nodes with each amount resolved to the viewed month's override (if
  // any). Ids and positions are unchanged, so editing and dragging still work.
  const storeNodes = useMemo(
    () => inputNodes.map((node) => withMonthlyOverrides(node, month)),
    [inputNodes, month],
  );

  const balanceById = useMemo(() => {
    const map = new Map<string, number>();
    for (const entry of balances) {
      map.set(entry.nodeId, entry.balance);
    }
    return map;
  }, [balances]);

  const nodeById = useMemo(() => {
    const map = new Map<string, VulpariNode>();
    for (const node of storeNodes) {
      map.set(node.id, node);
    }
    return map;
  }, [storeNodes]);

  // Total funded into each node this computation, so an account can show the
  // sum of what it covers (its "nice big number").
  const incomingByNode = useMemo(() => {
    const map = new Map<string, number>();
    for (const move of moves) {
      map.set(move.toNodeId, (map.get(move.toNodeId) ?? 0) + move.amount);
    }
    return map;
  }, [moves]);

  // Money flowing into each node this month in the primary currency, used for
  // group totals (so a group reflects everything wired into its members).
  const groupIncoming = useMemo(
    () =>
      incomingInPrimary(
        moves,
        (id) => nodeById.get(id)?.currency ?? primaryCurrency,
        primaryCurrency,
        rates,
      ),
    [moves, nodeById, primaryCurrency, rates],
  );

  const selectedNodeSet = useMemo(() => new Set(selectedNodeIds), [selectedNodeIds]);
  const selectedEdgeSet = useMemo(() => new Set(selectedEdgeIds), [selectedEdgeIds]);

  // A one-off only exists in its due month; outside it the node and its wires
  // are hidden entirely (rather than left floating) to keep the canvas calm.
  const isMonthHidden = useCallback(
    (id: string) => {
      const node = nodeById.get(id);
      return node?.type === "oneoff" && !node.meta.dueDate.startsWith(`${month}-`);
    },
    [nodeById, month],
  );
  // The node or group currently under a dragged node, highlighted as a potential
  // grouping target.
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  // Members of a collapsed group are hidden, and their connections rerouted to
  // the group's summary node.
  const hiddenToGroupNode = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of groups) {
      if (group.collapsed) {
        for (const nodeId of group.nodeIds) {
          map.set(nodeId, `${GROUP_PREFIX}${group.id}`);
        }
      }
    }
    return map;
  }, [groups]);

  const rfNodes = useMemo<Node<MoneyNodeData>[]>(
    () =>
      storeNodes
        .filter((node) => !hiddenToGroupNode.has(node.id) && !isMonthHidden(node.id))
        .map((node) => ({
          id: node.id,
          type: "money",
          position: { x: node.positionX, y: node.positionY },
          selected: selectedNodeSet.has(node.id),
          style: { width: estimateNodeWidth(node) },
          data: {
            node,
            computedBalance: balanceById.get(node.id),
            aggregate: incomingByNode.get(node.id),
            highlight: node.id === dropTargetId,
          },
        })),
    [
      storeNodes,
      balanceById,
      incomingByNode,
      selectedNodeSet,
      hiddenToGroupNode,
      dropTargetId,
      isMonthHidden,
    ],
  );

  // Backdrop nodes for groups, sized to bound their members (using a node
  // footprint estimate) and rendered behind them.
  const groupNodes = useMemo<Node[]>(() => {
    const nodeById2 = new Map(storeNodes.map((node) => [node.id, node]));
    return groups
      .map((group) => {
        const members = group.nodeIds
          .map((id) => nodeById2.get(id))
          .filter((node): node is VulpariNode => node !== undefined)
          // A one-off member that is hidden this month should not reserve space.
          .filter((node) => !isMonthHidden(node.id));
        if (members.length === 0) {
          return null;
        }
        const rects = members.map((node) => ({
          x: node.positionX,
          y: node.positionY,
          w: estimateNodeWidth(node),
          h: estimateNodeHeight(node, balanceById.get(node.id)),
        }));
        const minX = Math.min(...rects.map((rect) => rect.x));
        const minY = Math.min(...rects.map((rect) => rect.y));
        const maxX = Math.max(...rects.map((rect) => rect.x + rect.w));
        const maxY = Math.max(...rects.map((rect) => rect.y + rect.h));
        // Group total: the money flowing into the members this month, in the
        // ledger's primary currency (includes anything wired into a member).
        const total = members.reduce((sum, node) => sum + (groupIncoming.get(node.id) ?? 0), 0);
        const baseData = {
          name: group.name,
          color: group.color ?? "var(--colour-neutral)",
          memberIds: group.nodeIds,
          total,
          currency: primaryCurrency,
          count: members.length,
          highlight: `${GROUP_PREFIX}${group.id}` === dropTargetId,
        };

        if (group.collapsed) {
          // A solid summary card sitting where the group is, members hidden.
          return {
            id: `${GROUP_PREFIX}${group.id}`,
            type: "group",
            position: { x: minX, y: minY },
            data: { ...baseData, collapsed: true },
            draggable: true,
            selectable: false,
            zIndex: 0,
            style: { width: NODE_W, height: COLLAPSED_H },
          } as Node;
        }

        // An expanded, transparent backdrop hugging the members.
        return {
          id: `${GROUP_PREFIX}${group.id}`,
          type: "group",
          position: { x: minX - GROUP_PAD, y: minY - GROUP_TOP },
          data: { ...baseData, collapsed: false },
          // Draggable so the whole group can be moved by its backdrop, but not
          // part of node selection (its members carry the selection).
          draggable: true,
          selectable: false,
          zIndex: -1,
          // Transparent wrapper so only our rounded GroupNode backdrop shows.
          style: {
            width: maxX - minX + GROUP_PAD * 2,
            height: maxY - minY + GROUP_TOP + GROUP_PAD,
            backgroundColor: "transparent",
            border: "none",
          },
        } as Node;
      })
      .filter((node): node is Node => node !== null);
  }, [
    groups,
    storeNodes,
    balanceById,
    dropTargetId,
    isMonthHidden,
    groupIncoming,
    primaryCurrency,
  ]);

  const allNodes = useMemo<Node[]>(() => [...groupNodes, ...rfNodes], [groupNodes, rfNodes]);

  const rfEdges = useMemo<Edge[]>(() => {
    const remap = (id: string) => hiddenToGroupNode.get(id) ?? id;
    const edges: Edge[] = [];
    const seenSynthetic = new Set<string>();
    for (const flow of storeFlows) {
      // A one-off outside its due month is hidden, so are its wires.
      if (isMonthHidden(flow.sourceNodeId) || isMonthHidden(flow.targetNodeId)) {
        continue;
      }
      const source = remap(flow.sourceNodeId);
      const target = remap(flow.targetNodeId);
      if (source === target) {
        continue; // a connection internal to one collapsed group
      }
      const rerouted = source !== flow.sourceNodeId || target !== flow.targetNodeId;
      if (rerouted) {
        // A synthetic wire to/from a collapsed group; dedupe and keep it inert.
        const key = `${source}->${target}`;
        if (seenSynthetic.has(key)) {
          continue;
        }
        seenSynthetic.add(key);
        edges.push({
          id: `collapsed:${key}`,
          source,
          target,
          animated: true,
          selectable: false,
          style: { stroke: "var(--colour-neutral)", strokeWidth: 2 },
        });
      } else {
        const colour = edgeColour(nodeById.get(source), nodeById.get(target));
        edges.push({
          id: flow.id,
          source,
          target,
          animated: true,
          selected: selectedEdgeSet.has(flow.id),
          style: { stroke: colour, strokeWidth: 2 },
        });
      }
    }
    return edges;
  }, [storeFlows, nodeById, selectedEdgeSet, hiddenToGroupNode, isMonthHidden]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const removed: string[] = [];
      for (const change of changes) {
        // Group backdrops are moved via the drag handlers below, not here.
        if (change.type === "position" && change.position && !change.id.startsWith(GROUP_PREFIX)) {
          moveNode(change.id, change.position.x, change.position.y);
        } else if (change.type === "remove" && !change.id.startsWith(GROUP_PREFIX)) {
          removed.push(change.id);
        }
      }
      if (removed.length > 0) {
        void deleteNodes(removed);
      }
    },
    [moveNode, deleteNodes],
  );

  const onSelectionChange = useCallback(
    ({ nodes: selNodes, edges: selEdges }: OnSelectionChangeParams) => {
      setSelection(
        selNodes.map((node) => node.id),
        selEdges.map((edge) => edge.id),
      );
    },
    [setSelection],
  );

  // Drag a group's backdrop to move all of its members together.
  const groupDragPos = useRef<Record<string, { x: number; y: number }>>({});

  const onNodeDragStart = useCallback((_event: unknown, node: Node) => {
    if (node.id.startsWith(GROUP_PREFIX)) {
      groupDragPos.current[node.id] = { ...node.position };
    }
  }, []);

  const onNodeDrag = useCallback(
    (_event: unknown, node: Node) => {
      if (!node.id.startsWith(GROUP_PREFIX)) {
        // A money node: highlight any group/node it is hovering over.
        const state = useStore.getState();
        const dragged = state.nodes.find((n) => n.id === node.id);
        const target = dragged
          ? findGroupingTarget(state.nodes, state.groups, dragged, node.position.x, node.position.y)
          : null;
        setDropTargetId(target?.highlightId ?? null);
        return;
      }
      const prev = groupDragPos.current[node.id];
      if (!prev) {
        return;
      }
      const dx = node.position.x - prev.x;
      const dy = node.position.y - prev.y;
      if (dx === 0 && dy === 0) {
        return;
      }
      groupDragPos.current[node.id] = { ...node.position };
      const group = groups.find((g) => `${GROUP_PREFIX}${g.id}` === node.id);
      if (!group) {
        return;
      }
      const current = useStore.getState().nodes;
      for (const memberId of group.nodeIds) {
        const member = current.find((m) => m.id === memberId);
        if (member) {
          moveNode(memberId, member.positionX + dx, member.positionY + dy);
        }
      }
    },
    [groups, moveNode],
  );

  const onNodeDragStop = useCallback((_event: unknown, node: Node) => {
    setDropTargetId(null);
    if (node.id.startsWith(GROUP_PREFIX)) {
      delete groupDragPos.current[node.id];
      return;
    }

    // A node was dropped: propose a grouping if it landed on something, but only
    // ever propose it (the user confirms).
    const state = useStore.getState();
    const dragged = state.nodes.find((n) => n.id === node.id);
    if (!dragged) {
      return;
    }
    const target = findGroupingTarget(
      state.nodes,
      state.groups,
      dragged,
      node.position.x,
      node.position.y,
    );
    if (target) {
      state.setGroupPrompt(target.prompt);
    }
  }, []);

  const onNodeClick = useCallback(
    (event: MouseEvent, node: Node) => {
      const multi = event.shiftKey || event.metaKey || event.ctrlKey;
      if (node.id.startsWith(GROUP_PREFIX)) {
        const group = groups.find((g) => `${GROUP_PREFIX}${g.id}` === node.id);
        if (group) {
          setSelection(group.nodeIds, []);
        }
        return;
      }
      // Plain click selects just this node; modifier-clicks defer to React Flow's
      // own multi-selection via onSelectionChange.
      if (!multi) {
        setSelection([node.id], []);
      }
    },
    [groups, setSelection],
  );

  const onEdgeClick = useCallback(
    (event: MouseEvent, edge: Edge) => {
      const multi = event.shiftKey || event.metaKey || event.ctrlKey;
      if (!multi) {
        setSelection([], [edge.id]);
      }
    },
    [setSelection],
  );

  const onPaneClick = useCallback(() => setSelection([], []), [setSelection]);

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      for (const change of changes) {
        if (change.type === "remove") {
          void removeFlow(change.id);
        }
      }
    },
    [removeFlow],
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (connection.source && connection.target) {
        void connect(connection.source, connection.target);
      }
    },
    [connect],
  );

  return (
    <ReactFlow
      nodes={allNodes}
      edges={rfEdges}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      onSelectionChange={onSelectionChange}
      onNodeClick={onNodeClick}
      onEdgeClick={onEdgeClick}
      onPaneClick={onPaneClick}
      onNodeDragStart={onNodeDragStart}
      onNodeDrag={onNodeDrag}
      onNodeDragStop={onNodeDragStop}
      snapToGrid
      snapGrid={[16, 16]}
      fitView
      proOptions={{ hideAttribution: true }}
    >
      <Background color="var(--colour-node-border)" gap={16} />
      <Controls className="!border-node-border" />
    </ReactFlow>
  );
}
